import { useState, useMemo, useEffect, useContext } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import TopNavBar from '../components/navigation/TopNavBar'
import RoundedButton from '../components/buttons/RoundedButton'
import RoundedBorderButton from '../components/buttons/RoundedBorderButton'
import TwoButtonsModal from '../components/modals/TwoButtonsModal'
import LoadingModal from '../components/modals/LoadingModal'
import HarborSkillsTable from '../components/tables/HarborSkillsTable'
import * as Constants from '../utils/constants'
import { UserContext } from '../App'
import { isExpired } from 'react-jwt'
import { ToastContainer, toast } from 'react-toastify'
import arrow from '../assets/arrow.png'
import check from '../assets/check.png'
import strings from '../res/strings'
import 'react-toastify/dist/ReactToastify.css'

const EntityDetails = () => {
    const { id } = useParams()
    
    const [harbors, setHarbors] = useState([])
    const [entity, setEntity] = useState(null)

    const [showDisableEntityModal, setShowDisableEntityModal] = useState(false)
    const [showEnableEntityModal, setShowEnableEntityModal] = useState(false)
    const [showRemoveEntityModal, setShowRemoveEntityModal] = useState(false)

    const [isLoading, setIsLoading] = useState(true)

    const { loggedUser, setLoggedUser } = useContext(UserContext)
    const navigate = useNavigate()

    useEffect(() => {
        if(loggedUser !== null && loggedUser !== 'refresh') {
            const isTokenExpired = isExpired(loggedUser.jwtToken)

            if(isTokenExpired) {
                setLoggedUser(null)
                navigate('/')
            } else if(loggedUser.role !== Constants.Roles.ADMINIT) {
                navigate('/')
            } else {
                getHarbors()
                getEntityDetails()
            }
        } else if (loggedUser === null) {
            navigate('/')
        }
    }, [loggedUser])

    // Handle get harbors
    const getHarbors = () => {
        fetch('/api/Backoffice/ListHarbors', {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            if (response.status === 200) {
                const jsonRes = await response.json()

                setHarbors(jsonRes)
            } else {
                setTimeout(() => {
                    setIsLoading(false)
                    showErrorToast(strings.getDropdownsError)

                    setHarbors([])
                }, 1000)
            }

        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setHarbors([])
            }, 1000)   
        })
    }

    // Handle entity details
    const getEntityDetails = () => {
        fetch(`/api/Backoffice/${id}/DetailsEntity`, {
            method:'GET',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)

                if (response.status === 200) {
                    const jsonRes = await response.json()
                    
                    setEntity(jsonRes)
                } else {
                    showErrorToast(strings.getEntityDetailsError)
                    setEntity(null)
                }
                
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)

                setEntity(null)
            }, 1000)
        })
    }

    // Check the harbor and show matching text
    const showHarborText = (harborId) => {
        const harbor = harbors && harbors.find((harbor) => harbor.id === harborId)
        return harbors && harbor.name
    }

    const columnsHarborSkills = useMemo(() => [
        {
            name: strings.harbor,
            cell: row => showHarborText(row.harborId),
            selector: row => showHarborText(row.harborId)
        },
        {
            name: <div><p className='font-normal'>{strings.fairing}</p><p>{strings.putToDry}</p></div>,
            selector: row => row.skillIds.some((skill) => skill === Constants.SKILLS.fairingPutToDry) ? <img className='h-5 w-5' src={check} alt='Check'/> : null
        },
        {
            name: <div><p className='font-normal'>{strings.fairing}</p><p>{strings.putToSwim}</p></div>,
            selector: row => row.skillIds.some((skill) => skill === Constants.SKILLS.fairingPutToSwim) ? <img className='h-5 w-5' src={check} alt='Check'/> : null
        },
        {
            name: <div><p className='font-normal'>{strings.fairing}</p><p>{strings.lineChange}</p></div>,
            selector: row => row.skillIds.some((skill) => skill === Constants.SKILLS.fairingLineChange) ? <img className='h-5 w-5' src={check} alt='Check'/> : null
        },
        {
            name: strings.electricitySupply2,
            selector: row => row.skillIds.some((skill) => skill === Constants.SKILLS.electricitySupply) ? <img className='h-5 w-5' src={check} alt='Check'/> : null
        },
        {
            name: strings.accessFacilities,
            selector: row => row.skillIds.some((skill) => skill === Constants.SKILLS.accessToFacilities) ? <img className='h-5 w-5' src={check} alt='Check'/> : null
        },
        {
            name: strings.mooring,
            selector: row => row.skillIds.some((skill) => skill === Constants.SKILLS.mooring) ? <img className='h-5 w-5' src={check} alt='Check'/> : null
        },
    ], [harbors])

    // Handles date formatting
    const formatDate = (timeStamp) => {
        const date = new Date(timeStamp)
        const day = date.getDate()
        const month = date.getMonth() + 1
        const year = date.getFullYear()
        return `${day < 10 ? '0' + day : day}/${month < 10 ? '0' + month : month}/${year}`
    }

    // Show disable entity modal
    const onClickDisable = () => {
        setShowDisableEntityModal(true)
    }

    // Handles disabled entity
    const disableEntity = () => {
        setShowDisableEntityModal(false)
        setIsLoading(true)

        // Disable entity
        fetch(`/api/BackOffice/${id}/DeactivateEntity`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
    
                if (response.status === 200) {
                    setIsLoading(true)
                    getEntityDetails()
                } else if (response.status === 404) {
                    showErrorToast(strings.entityNotFound)
                } else if (response.status === 401) {
                    showErrorToast(strings.notAuthorizedEntity)
                } else {
                    showErrorToast(strings.disableEntityError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })  
    }

    // Show enable entity modal
    const onClickEnable = () => {
        setShowEnableEntityModal(true)
    }

    // Handles enable entity
    const enableEntity = () => {
        setShowEnableEntityModal(false)
        setIsLoading(true)

        // Enable entity
        fetch(`/api/BackOffice/${id}/ActivateEntity`, {
            method:'PUT',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
        
                if (response.status === 200) {
                    setIsLoading(true)
                    getEntityDetails()
                } else if (response.status === 404) {
                    showErrorToast(strings.entityNotFound)
                } else if (response.status === 401) {
                    showErrorToast(strings.notAuthorizedEntity)
                } else {
                    showErrorToast(strings.enableEntityError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })
    }

    // Show remove entity modal
    const onClickRemove = () => {
        setShowRemoveEntityModal(true)
    }

    // Handles remove entity
    const removeEntity = () => {
        setShowRemoveEntityModal(false)
        setIsLoading(true)

        // Remove user
        fetch(`/api/BackOffice/${id}/RemoveEntity`, {
            method:'DELETE',
            headers: { 
              'Accept': 'application/json',
              'Content-Type': 'application/json; charset=UTF-8',
              'Authorization': 'Bearer ' + loggedUser.jwtToken
            }, 
            mode: 'cors'
        }).then(async response => {
            setTimeout(async () => {
                setIsLoading(false)
        
                if (response.status === 200) {
                    navigate('/backoffice-entities')
                } else if (response.status === 404) {
                    showErrorToast(strings.entityNotFound)
                } else {
                    showErrorToast(strings.deleteEntityError)
                }
            }, 1000)
    
        }).catch((error) => {
            setTimeout(() => {
                setIsLoading(false)
                showErrorToast(error)
            }, 1000)
        })
    }

    // Navigate to update entity
    const onClickUpdate = () => {
        navigate(`/edit-entity/${entity.id}`)
    }

    // Handle show error toast
    const showErrorToast = (error) => {
        toast.error(error, {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: false,
            draggable: false,
            progress: undefined,
            theme: 'colored'
        })
    }

    return (
        <>
            <TopNavBar />

            <div className='flex flex-col px-[12.5rem] min-h-screen mb-16'>

                { isLoading ?
                    <LoadingModal />
                :   <>
                        <div className='flex flex-row items-center mt-16 mb-12 space-x-2'>
                            <button onClick={() => navigate(-1)}>
                                <img className='h-5 w-5' src={arrow} alt='Voltar atrás'/>
                            </button>
                            <p className='font-montserrat font-semibold text-accent'>{strings.entityManagement} / {entity && entity.name}</p>
                        </div>

                        <p className='font-montserrat text-xl font-bold text-navy'>{strings.info}</p>

                        <div className='flex flex-row mt-6'>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{strings.name2}</p>
                                <p className='font-montserrat text-xl font-semibold'>{entity && entity.name}</p>
                            </div>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{strings.nif}</p>
                                <p className='font-montserrat text-xl font-semibold'>{entity && entity.nif}</p>
                            </div>
                        </div>

                        <div className='flex flex-row mt-6'>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{strings.concession}</p>
                                <p className='font-montserrat text-xl font-semibold'>{entity && entity.contractor}</p>
                            </div>
                            <div className='flex flex-col w-1/2'>
                                <p className='font-montserrat text-sm'>{strings.state}</p>
                                <p className='font-montserrat text-xl font-semibold'>{entity && entity.active ? strings.active : strings.inactive}</p>
                            </div>
                        </div>

                        <p className='font-montserrat text-sm mt-6'>{strings.startDateConcession}</p>
                        <p className='font-montserrat text-xl font-semibold'>{entity && (formatDate(entity.date))}</p>

                        <p className='font-montserrat text-xl font-bold text-navy mt-16 mb-6'>{strings.harborSkills}</p>

                        {entity && entity.harborSkills.length > 0 ?
                            <HarborSkillsTable
                                columns={columnsHarborSkills}
                                data={entity && entity.harborSkills} 
                            />
                        :   <p className='font-montserrat text-neutral5'>{strings.noInfo}</p>}

                        <div className='flex flex-row items-center space-x-4 mt-20'>

                            {entity && entity.active ?
                                <div className='flex w-60'>
                                    <RoundedButton 
                                        onClick={onClickUpdate}
                                        text={strings.updateEntity}
                                        height={48}
                                        bgColor='#007EB4'
                                    />
                                </div>
                            : null}

                            <div className='flex w-60'>
                                <RoundedBorderButton
                                    text={entity && (entity.active ? strings.disableEntity : strings.enableEntity)}
                                    onClick={entity && (entity.active ? onClickDisable : onClickEnable)}
                                    height={48}
                                    fontWeight='600'
                                />
                            </div>

                            <div className='flex w-60'>
                                <RoundedButton 
                                    onClick={onClickRemove}
                                    text={strings.removeEntity}
                                    height={48}
                                    bgColor='#E85F5C'
                                />
                            </div>

                        </div>
                    </>
                }

            </div>

            <TwoButtonsModal 
                open={showDisableEntityModal}
                onClose={() => setShowDisableEntityModal(false)}
                title={strings.disableEntity}
                message={strings.disableEntityMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.disable}
                onClick={disableEntity}
            />

            <TwoButtonsModal 
                open={showEnableEntityModal}
                onClose={() => setShowEnableEntityModal(false)}
                title={strings.enableEntity}
                message={strings.enableEntityMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.enable}
                onClick={enableEntity}
            />

            <TwoButtonsModal 
                open={showRemoveEntityModal}
                onClose={() => setShowRemoveEntityModal(false)}
                title={strings.removeEntity}
                message={strings.removeEntityMessage}
                buttonText1={strings.cancel}
                buttonText2={strings.remove}
                onClick={removeEntity}
            />

            <ToastContainer />
        </>
    )
}

export default EntityDetails